import { sanitizeFilter } from './filterManager';
import { SavedSearchConfiguration } from '../types';

const initializeColumnSettings = (columns: any[]): Record<string, boolean> => {
    const settings: Record<string, boolean> = {};
    if (columns) {
        columns.forEach((col: any) => {
            const colId = col.id || col.command;
            if (colId && col.toggleable) {
                settings[colId] = col.hidden || false;
            }
        });
    }
    return settings;
};

const applyColumnVisibility = (config: SavedSearchConfiguration, columns: any[], settings: Record<string, boolean>): Record<string, boolean> => {
    if (config.columns?.visible) {
        config.columns.visible.forEach((id: string) => {
            const col = columns?.find((c: any) => (c.id || c.command) === id);
            if (col && col.toggleable) {
                settings[id] = false;
            }
        });
    }
    if (config.columns?.hidden) {
        config.columns.hidden.forEach((id: string) => {
            const col = columns?.find((c: any) => (c.id || c.command) === id);
            if (col && col.toggleable) {
                settings[id] = true;
            }
        });
    }
    return settings;
};

const createTableInterface = (context: any) => {
    const { table, self } = context;

    return {
        replaceFilters: (filters: any[]) => {
            try {
                const sanitized = filters.map((f: any) => sanitizeFilter({ filters: [f] }).filters[0]).filter(Boolean);

                if (self) {
                    self.replaceFilters(sanitized);
                } else if (table?.extensions?.replaceFilters) {
                    table.extensions.replaceFilters(sanitized);
                } else {
                    console.warn('No replaceFilters method available on table interface');
                }
            } catch (error) {
                console.error('Error replacing filters:', error);
            }
        },

        setState: (state: any) => {
            try {
                if (self) {
                    self.setState(state);
                } else if (table?.extensions?.setState) {
                    table.extensions.setState(state);
                } else {
                    console.warn('No setState method available on table interface');
                }
            } catch (error) {
                console.error('Error setting table state:', error);
            }
        },

        getState: (): any => {
            if (self) {
                return self.state;
            } else if (table?.state) {
                return table.state;
            }
            return {};
        },

        getData: () => {
            try {
                if (self?.getData) {
                    self.getData();
                } else if (table?.extensions?.getData) {
                    table.extensions.getData();
                } else {
                    console.warn('No getData method available on table interface');
                }
            } catch (error) {
                console.error('Error getting table data:', error);
            }
        },

        isValid: (): boolean => {
            return !!(self || table?.extensions);
        }
    };
};

export function applySavedSearchConfig(config: SavedSearchConfiguration, context: any = {}): void {
    const { columns, setConfig, table, self } = context;
    const tableInterface = createTableInterface(context);

    if (!tableInterface.isValid()) {
        console.warn('Invalid table interface in applySavedSearchConfig');
        return;
    }

    const hasBatchedState = !!(self?.applyBatchedState || table?.extensions?.applyBatchedState);

    if (hasBatchedState) {
        if ((config.searchTerm !== undefined || config.customFields) && setConfig) {
            setConfig((prev: any) => {
                const updates: any = {
                    ...prev,
                    term: config.searchTerm !== undefined ? config.searchTerm : prev.term,
                    _skipRefresh: true  // Prevent componentDidUpdate from triggering refresh
                };

                // Apply custom fields
                if (config.customFields) {
                    updates.customFields = config.customFields;
                    // Also apply individual custom fields to the root config for backward compatibility
                    Object.keys(config.customFields).forEach((key: string) => {
                        updates[key] = (config.customFields as any)[key];
                    });
                }

                return updates;
            });
        }

        const batchedChanges: any = {};

        if (config.filters?.filters) {
            // Note: config.filters should already contain merged system and user filters from useSavedSearch
            const sanitized = config.filters.filters
                .map((f: any) => sanitizeFilter({ filters: [f] }).filters[0])
                .filter(Boolean);
            batchedChanges.filters = sanitized;
        }

        if (config.sorting) {
            batchedChanges.sorting = config.sorting;
        }

        if (config.pagination?.pageSize) {
            batchedChanges.pagination = { pageSize: config.pagination.pageSize };
        }

        if (config.columns && columns) {
            try {
                let newColumnSettings = initializeColumnSettings(columns);
                newColumnSettings = applyColumnVisibility(config, columns, newColumnSettings);
                batchedChanges.columnSettings = newColumnSettings;
            } catch (error) {
                console.error('Error applying column configuration:', error);
            }
        }

        try {
            if (self?.applyBatchedState) {
                self.applyBatchedState(batchedChanges);
            } else if (table?.extensions?.applyBatchedState) {
                table.extensions.applyBatchedState(batchedChanges);
            }
        } catch (error) {
            console.error('Error applying batched state:', error);
        }

    } else {
        if (config.filters?.filters) {
            tableInterface.replaceFilters(config.filters.filters);
        }

        if (config.sorting) {
            const currentState = tableInterface.getState();
            tableInterface.setState({
                orderBy: config.sorting.orderBy || currentState.orderBy,
                order: config.sorting.order || currentState.order
            });
        }

        if (config.pagination?.pageSize) {
            tableInterface.setState({
                rowsPerPage: config.pagination.pageSize
            });
        }

        if ((config.searchTerm !== undefined || config.customFields) && setConfig) {
            setConfig((prev: any) => {
                const updates: any = {
                    ...prev,
                    term: config.searchTerm !== undefined ? config.searchTerm : prev.term
                };

                // Apply custom fields
                if (config.customFields) {
                    updates.customFields = config.customFields;
                    // Also apply individual custom fields to the root config for backward compatibility
                    Object.keys(config.customFields).forEach((key: string) => {
                        updates[key] = (config.customFields as any)[key];
                    });
                }

                return updates;
            });
        }

        if (config.columns && columns) {
            try {
                let newColumnSettings = initializeColumnSettings(columns);
                newColumnSettings = applyColumnVisibility(config, columns, newColumnSettings);
                tableInterface.setState({ columnSettings: newColumnSettings });
            } catch (error) {
                console.error('Error applying column configuration:', error);
            }
        }

        tableInterface.getData();
    }
}
